// Ghost cursor trail mode controller.
//
// Faithful port of ghost-cursor-trail by jakaria-istauk (MIT), itself based on
// supahfunk's CodePen. Renders a gooey CSS ghost that trails behind the cursor
// with squash/stretch, eye squint, and a mouth that opens on click.
//
// The ghost owns its own position (no walk FSM) and is not draggable.

import type { ControllerContext, Mood, PetController, Vec2 } from './types';

// --- Configuration (matches ghost-cursor-trail defaults) ---
const FOLLOW_SPEED = 10;
const VELOCITY_MULTIPLIER = 8;
const GHOST_SCALE = 0.7;
const GHOST_CSS_WIDTH = 90;

const STYLE_ID = 'site-pet-ghost-style';

const GHOST_HTML = `
<div class="ghost-cursor-trail__head">
  <div class="ghost-cursor-trail__eyes"></div>
  <div class="ghost-cursor-trail__mouth"></div>
</div>
<div class="ghost-cursor-trail__tail">
  <div class="ghost-cursor-trail__rip"></div>
</div>
<svg class="ghost-cursor-trail-svg" aria-hidden="true">
  <defs>
    <filter id="ghost-cursor-goo">
      <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="ghost-blur" />
      <feColorMatrix in="ghost-blur" mode="matrix"
        values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 16 -7" result="ghost-gooey" />
    </filter>
  </defs>
</svg>`;

const GHOST_CSS = `
.site-pet-ghost {
  position: fixed;
  z-index: 9998;
  pointer-events: none !important;
  cursor: default !important;
  transform-origin: center;
  width: ${GHOST_CSS_WIDTH}px;
  height: auto;
  margin: 20px 0 0 ${-GHOST_CSS_WIDTH / 2}px;
  top: 0;
  left: 0;
  will-change: transform;
}
.site-pet-ghost .ghost-cursor-trail__head {
  position: relative;
  width: 100%;
  height: ${GHOST_CSS_WIDTH}px;
  background: var(--ghost-color, #ffffff);
  border-radius: ${GHOST_CSS_WIDTH / 2}px ${GHOST_CSS_WIDTH / 2}px 0 0;
}
.site-pet-ghost .ghost-cursor-trail__eyes {
  position: absolute;
  z-index: 1;
  width: 15px;
  height: 15px;
  top: 34px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50px;
  background: var(--eye-color, #161616);
  margin-left: -20px;
  transform-origin: center;
  box-shadow: 40px 0 0 var(--eye-color, #161616);
  transition: r 0.18s ease-out;
}
.site-pet-ghost .ghost-cursor-trail__mouth {
  position: absolute;
  z-index: 1;
  margin: 0;
  top: 60px;
  left: 50%;
  width: 20px;
  height: 15px;
  border-radius: 20px 20px 12px 12px;
  background: var(--eye-color, #161616);
  transform: translateX(-50%) scale(0);
  transform-origin: center bottom;
  overflow: hidden;
  transition: transform 0.12s ease-out;
}
.site-pet-ghost .ghost-cursor-trail__tail {
  position: absolute;
  z-index: -1;
  top: 82px;
  height: 55px;
  width: 100%;
  filter: url(#ghost-cursor-goo);
}
.site-pet-ghost .ghost-cursor-trail__tail::before {
  content: "";
  background: var(--ghost-color, #ffffff);
  position: absolute;
  bottom: 35px;
  left: 0;
  height: 100px;
  width: 100%;
  border-radius: 40px 40px 5px 5px;
}
.site-pet-ghost .ghost-cursor-trail__rip {
  width: 15px;
  height: 28px;
  background: var(--ghost-color, #ffffff);
  position: absolute;
  top: 15px;
  left: 0;
  box-shadow:
    -62px 0 0 var(--ghost-color, #ffffff),
    -31px 0 0 var(--ghost-color, #ffffff),
    31px 0 0 var(--ghost-color, #ffffff),
    62px 0 0 var(--ghost-color, #ffffff),
    93px 0 0 var(--ghost-color, #ffffff);
  border-radius: 50%;
  animation: ghost-cursor-rips 1.2s linear infinite;
}
.site-pet-ghost .ghost-cursor-trail-svg {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
}

@keyframes ghost-cursor-rips {
  0%   { left: 0;   top: 12px; }
  50%  { left: 31px; top: 20px; }
  100% { left: 62px; top: 12px; }
}

/* Mood expression overrides */
.site-pet-ghost[data-mood="sleeping"] .ghost-cursor-trail__eyes {
  height: 3px;
  top: 40px;
  box-shadow: 40px 0 0 var(--eye-color, #161616);
}
.site-pet-ghost[data-mood="happy"] .ghost-cursor-trail__mouth,
.site-pet-ghost[data-mood="playful"] .ghost-cursor-trail__mouth {
  transform: translateX(-50%) scale(0.6);
}
.site-pet-ghost[data-mood="startled"] .ghost-cursor-trail__eyes,
.site-pet-ghost[data-mood="alert"] .ghost-cursor-trail__eyes {
  width: 18px;
  height: 18px;
  margin-left: -22px;
  box-shadow: 44px 0 0 var(--eye-color, #161616);
}
.site-pet-ghost[data-mood="startled"] .ghost-cursor-trail__mouth {
  transform: translateX(-50%) scale(1.2);
}
.site-pet-ghost[data-mood="sad"] .ghost-cursor-trail__mouth {
  border-radius: 12px 12px 20px 20px;
  top: 62px;
}
.site-pet-ghost[data-mood="thinking"] .ghost-cursor-trail__eyes {
  margin-left: -18px;
  box-shadow: 36px -2px 0 var(--eye-color, #161616);
}

@media (prefers-reduced-motion: reduce) {
  .site-pet-ghost .ghost-cursor-trail__rip { animation: none; }
}`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = GHOST_CSS;
  document.head.appendChild(style);
}

function map(num: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  return ((num - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

export function createGhostController(ctx: ControllerContext): PetController {
  const { el, reducedMotion } = ctx;

  ensureStyle();
  el.classList.add('site-pet-ghost');
  el.setAttribute('data-mood', 'idle');
  el.innerHTML = GHOST_HTML;

  el.style.left = '0px';
  el.style.top = '0px';

  const eyesEl = el.querySelector<HTMLElement>('.ghost-cursor-trail__eyes');
  const mouthEl = el.querySelector<HTMLElement>('.ghost-cursor-trail__mouth');

  let curX = ctx.initialPos.x;
  let curY = ctx.initialPos.y;
  let targetX = curX;
  let targetY = curY;
  let clicked = false;

  const onMove = (e: MouseEvent) => {
    targetX = e.clientX;
    targetY = e.clientY;
  };
  document.addEventListener('mousemove', onMove, { passive: true });

  const onDown = () => { clicked = true; };
  const onUp = () => { clicked = false; };
  document.addEventListener('mousedown', onDown, { passive: true });
  document.addEventListener('mouseup', onUp, { passive: true });

  return {
    drivesPosition: false,
    draggable: false,

    setMood(mood: Mood) {
      el.setAttribute('data-mood', mood);
    },

    tick(_dtMs: number) {
      const distX = targetX - curX;
      const distY = targetY - curY;

      const vx = distX / VELOCITY_MULTIPLIER;
      const vy = distY / VELOCITY_MULTIPLIER;

      if (!reducedMotion) {
        curX += distX / FOLLOW_SPEED;
        curY += distY / FOLLOW_SPEED;
      } else {
        curX = targetX;
        curY = targetY;
      }

      const skewX = map(vx, 0, 100, 0, -50);
      const absVx = Math.abs(vx);
      const absVy = Math.abs(vy);

      const scaleY = map(absVy, 0, 100, 1, 2.0);
      const scaleEyeX = map(absVx, 0, 100, 1, 1.2);
      let scaleEyeY = map(absVx * 2, 0, 100, 1, 0.1);
      let scaleMouth = Math.min(
        Math.max(
          map(absVx * 1.5, 0, 100, 0, 10),
          map(absVy * 1.2, 0, 100, 0, 5),
        ),
        2,
      );

      if (clicked) {
        scaleEyeY = 0.4;
        scaleMouth = -scaleMouth;
      }

      el.style.transform =
        `translate3d(${curX}px, ${curY}px, 0) scale(${GHOST_SCALE}) skew(${skewX}deg) rotate(${-skewX}deg) scaleY(${scaleY})`;

      if (eyesEl) {
        eyesEl.style.transform = `translateX(-50%) scale(${scaleEyeX}, ${scaleEyeY})`;
      }
      if (mouthEl) {
        mouthEl.style.transform = `translateX(-50%) scale(${scaleMouth})`;
      }
    },

    getPosition(): Vec2 {
      return { x: curX, y: curY };
    },

    destroy() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
      el.classList.remove('site-pet-ghost');
      el.removeAttribute('data-mood');
      el.style.transform = '';
      el.style.left = '';
      el.style.top = '';
      el.innerHTML = '';
    },
  };
}
